import { FormattedMessage, useIntl } from 'react-intl';
import { useEffect, useState, useRef } from 'react';
import BlockTitle from '../../../layout/BlockTitle';
import TextInput from '../../../../Enter/otc/components/TextInput';
import { Button } from '../../../../common/Button';
import { sendRenameRouterRequest } from '../../../../../utils/api/routerAPI';
import Loader from '../../../../common/Loader';
import { useNotificationContext } from '../../../layout/FloatingNotification';
import checkIcon from '../../../../../../assets/icons/check.svg';
import crossIcon from '../../../../../../assets/icons/devices-router/cross-icon.svg';
import styles from './RouterNameBlock.scss';

interface RouterNameBlockProps {
  value: string;
  onChange: ({ value }: { value: string }) => void;
  editedRouterId?: string;
  onRouterRenamed?: () => void;
}

const RouterNameBlock = ({ value, onChange, editedRouterId, onRouterRenamed }: RouterNameBlockProps) => {
  const intl = useIntl();

  const { addNotification } = useNotificationContext();

  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [editedValue, setEditedValue] = useState(value);
  const [inputError, setInputError] = useState(false);
  const onEditInputChange = ({ value: editIntputValue }: { value: string }) => {
    setEditedValue(editIntputValue);

    if (inputError) {
      setInputError(false);
    }
  };

  useEffect(() => {
    if (!isEditing) {
      setEditedValue(value);
    }
  }, [value]);

  const onEditSubmit = () => {
    // no edited router ID means this is a new router, no separate API call needed
    if (!editedRouterId) {
      onChange({ value: editedValue });
      setIsEditing(false);

      if (editedValue.length > 0) {
        addNotification('routerNameChanged');
      }

      return;
    }

    if (editedValue.length === 0) {
      setInputError(true);

      return;
    }

    setIsLoading(true);
    setIsEditing(false);
    sendRenameRouterRequest(editedRouterId, editedValue)
      .then(
        () => {
          onChange({ value: editedValue });
          addNotification('routerNameChanged');
          onRouterRenamed?.();
        },
        () => {
          addNotification('routerNameChangeError');
        },
      )
      .finally(() => {
        setIsEditing(false);
        setIsLoading(false);
      });
  };

  const textInputRef = useRef<HTMLInputElement>(null);

  return (
    <article>
      <BlockTitle className={styles.title}>
        <FormattedMessage id="profile.routerSection.nameBlock.title" defaultMessage="Device name" />
      </BlockTitle>
      <div className={styles.fieldWrapper}>
        <TextInput
          hideError
          error={inputError && isEditing}
          value={isEditing || isLoading ? editedValue : value}
          handleChange={isEditing ? onEditInputChange : onChange}
          ref={textInputRef}
          maxLength={50}
          className={styles.textInput}
          autoComplete="off"
          placeholder={intl.formatMessage({
            id: 'profile.routerSection.nameBlock.placeholder',
            defaultMessage: 'For example, Home router',
          })}
          rightSideSlot={
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <g id="Icon-container">
                <path
                  id="Union"
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M19 3.17139C18.7599 3.17139 18.5222 3.21868 18.3003 3.31057C18.0785 3.40245 17.8769 3.53714 17.7071 3.70692L4.39491 17.0191L3.42524 20.5746L6.9807 19.6049L20.2929 6.29271C20.4627 6.12292 20.5974 5.92136 20.6893 5.69952C20.7812 5.47769 20.8284 5.23993 20.8284 4.99981C20.8284 4.7597 20.7812 4.52194 20.6893 4.30011C20.5974 4.07827 20.4627 3.87671 20.2929 3.70692C20.1231 3.53714 19.9216 3.40245 19.6997 3.31057C19.4779 3.21868 19.2401 3.17139 19 3.17139ZM17.5349 1.46281C17.9994 1.27041 18.4973 1.17139 19 1.17139C19.5028 1.17139 20.0006 1.27041 20.4651 1.46281C20.9296 1.6552 21.3516 1.9372 21.7071 2.29271C22.0626 2.64821 22.3446 3.07025 22.537 3.53474C22.7294 3.99923 22.8284 4.49706 22.8284 4.99981C22.8284 5.50257 22.7294 6.0004 22.537 6.46489C22.3446 6.92938 22.0626 7.35142 21.7071 7.70692L8.20713 21.2069C8.08407 21.33 7.93104 21.4188 7.76314 21.4646L2.26314 22.9646C1.91693 23.059 1.54667 22.9607 1.29292 22.7069C1.03917 22.4532 0.940838 22.0829 1.03526 21.7367L2.53526 16.2367C2.58105 16.0688 2.66986 15.9158 2.79292 15.7927L16.2929 2.29271C16.6484 1.9372 17.0705 1.6552 17.5349 1.46281Z"
                  fill="#212231"
                  fillOpacity="0.64"
                />
              </g>
            </svg>
          }
        />
        {!isEditing && (
          <button
            type="button"
            className={styles.coverEditButton}
            onClick={() => {
              setIsEditing(true);
              textInputRef.current?.focus();
            }}
          />
        )}
        {isEditing && (
          <div className={styles.editControls}>
            <Button kind="primary" onClick={onEditSubmit}>
              <img src={checkIcon} width={20} height={20} alt="" />
            </Button>
            <Button
              onClick={() => {
                setIsEditing(false);
                setEditedValue(value);
                setInputError(false);
              }}
            >
              <img src={crossIcon} width={24} height={24} alt="" />
            </Button>
          </div>
        )}
        {isLoading && (
          <div className={styles.editControls}>
            <Loader colored />
          </div>
        )}
      </div>
    </article>
  );
};

export default RouterNameBlock;
